import { useEffect, useState } from "react";
import { AppShell } from "@/components/Nav";
import { PlusGate } from "@/components/PlusGate";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Scale, Sparkles, Copy, Download } from "lucide-react";

export default function CfpbEscalation() {
  const { user } = useAuth();
  const isPlus = user?.plan === "plus";

  const [items, setItems] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [selectedItem, setSelectedItem] = useState("");
  const [bureau, setBureau] = useState("equifax");
  const [priorNotes, setPriorNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    if (!isPlus) return;
    try {
      const [it, cp] = await Promise.all([
        api.get("/negative-items"),
        api.get("/plus/cfpb-complaints"),
      ]);
      setItems(it.data);
      setComplaints(cp.data);
      if (it.data.length && !selectedItem) setSelectedItem(it.data[0].id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, [isPlus]);

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedItem) {
      toast.error("Pick a negative item first");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/plus/cfpb-complaint", {
        negative_item_id: selectedItem,
        bureau,
        prior_dispute_notes: priorNotes || null,
      });
      setResult(data);
      load();
      toast.success("CFPB complaint drafted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    if (!result?.complaint) return;
    navigator.clipboard.writeText(result.complaint);
    toast.success("Copied");
  };

  const download = () => {
    if (!result?.complaint) return;
    const blob = new Blob([result.complaint], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cfpb-${bureau}-${result.creditor_snapshot || "complaint"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppShell>
      <div className="mb-10">
        <div className="overline mb-3 text-[#D5FF3F]">Plus tool</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">CFPB escalation.</h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl">
          When direct disputes stall, file a formal complaint with the Consumer Financial Protection Bureau —
          bureaus and furnishers respond within 15 days by law.
        </p>
      </div>

      {!isPlus ? (
        <PlusGate
          toolName="CFPB escalation templates"
          tagline="Direct disputes fail all the time. A CFPB complaint forces a 15-day response and puts the bureau on notice. Aura Plus drafts them for you."
        />
      ) : (
        <>
          <form onSubmit={submit} className="card-surface p-8 space-y-5 mb-6" data-testid="cfpb-form">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Item to escalate</Label>
              <Select value={selectedItem} onValueChange={setSelectedItem}>
                <SelectTrigger data-testid="cfpb-item-select" className="mt-1 rounded-sm bg-[#0B0C0E] border-[#26282C]">
                  <SelectValue placeholder="Choose an item" />
                </SelectTrigger>
                <SelectContent className="bg-[#131518] border-[#26282C] text-[#F5F3EE]">
                  {items.length === 0 ? (
                    <SelectItem value="none" disabled>No items yet — add one first</SelectItem>
                  ) : items.map((it) => (
                    <SelectItem key={it.id} value={it.id}>
                      {it.creditor} · {(it.item_type || "").replace("_", " ")}
                      {it.amount ? ` · $${it.amount}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Bureau (or furnisher)</Label>
              <Select value={bureau} onValueChange={setBureau}>
                <SelectTrigger data-testid="cfpb-bureau-select" className="mt-1 rounded-sm bg-[#0B0C0E] border-[#26282C]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#131518] border-[#26282C] text-[#F5F3EE]">
                  <SelectItem value="equifax">Equifax</SelectItem>
                  <SelectItem value="experian">Experian</SelectItem>
                  <SelectItem value="transunion">TransUnion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Prior dispute notes (optional)</Label>
              <Textarea
                rows={4}
                placeholder="e.g. Direct dispute sent to Equifax on 2026-01-05, marked 'verified' without any method of verification supplied."
                value={priorNotes}
                onChange={(e) => setPriorNotes(e.target.value)}
                data-testid="cfpb-notes"
                className="mt-1 rounded-sm"
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={loading || items.length === 0}
                data-testid="cfpb-submit-btn"
                className="rounded-full btn-lime h-11 px-6"
              >
                {loading ? (
                  <><Sparkles size={15} className="mr-2 animate-pulse" /> Drafting…</>
                ) : (
                  <><Scale size={15} className="mr-2" /> Draft complaint</>
                )}
              </Button>
            </div>
          </form>

          {result && (
            <div className="card-surface p-8 lg:p-10 fade-in" data-testid="cfpb-result">
              <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <div>
                  <div className="overline mb-2">CFPB complaint · draft</div>
                  <div className="font-display text-2xl">{result.creditor_snapshot} · {result.bureau}</div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={copy} data-testid="cfpb-copy" className="rounded-sm btn-outline">
                    <Copy size={13} className="mr-2" /> Copy
                  </Button>
                  <Button onClick={download} data-testid="cfpb-download" className="rounded-sm btn-outline">
                    <Download size={13} className="mr-2" /> Download
                  </Button>
                </div>
              </div>
              <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-[#F5F3EE]">
{result.complaint}
              </pre>
              <div className="mt-6 text-xs text-[#6D716C] leading-relaxed">
                File this at <a href="https://www.consumerfinance.gov/complaint/" target="_blank" rel="noopener noreferrer" className="text-[#D5FF3F] underline">consumerfinance.gov/complaint</a>.
                The CFPB forwards it to the company, which must respond within 15 days.
              </div>
            </div>
          )}

          {complaints.length > 0 && (
            <div className="mt-10">
              <div className="overline mb-4">Recent complaints</div>
              <div className="space-y-2">
                {complaints.slice(0, 10).map((c) => (
                  <div key={c.id} className="card-surface p-4 flex items-center justify-between">
                    <div>
                      <div className="text-sm text-[#F5F3EE]">{c.creditor_snapshot} · {c.bureau}</div>
                      <div className="text-xs text-[#6D716C] font-mono">{new Date(c.created_at).toLocaleDateString()}</div>
                    </div>
                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(c.complaint);
                        toast.success("Copied");
                      }}
                      className="rounded-sm btn-outline h-8"
                    >
                      <Copy size={12} className="mr-2" /> Copy
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </AppShell>
  );
}
